/**
 * DeliverySummaryTab Component
 * 배달 요약 탭 (기존 홈 페이지)
 */
import React, { useRef, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatsCard } from './shared/StatsCard';
import { FeeCard } from './shared/FeeCard';
import { SlotsCard } from './shared/SlotsCard';
import { useHomePageForRider } from '../../hooks/useHomePageForRider';
import { useDeferredLoading } from '../../../../core/hooks/useDeferredLoading';
import { usePullToRefresh } from '../../../../core/hooks/usePullToRefresh';
import { getUserInfo } from '../../../../core/utils';
import styles from '../styles/DeliverySummaryTab.module.css';

type TabType = 'summary' | 'detail';

interface DeliverySummaryTabProps {
    activeTab: TabType;
    onTabChange: (tab: TabType) => void;
}

export const DeliverySummaryTab: React.FC<DeliverySummaryTabProps> = ({ activeTab, onTabChange }) => {
    const navigate = useNavigate();
    const dateRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<Element | null>(null);

    const isSupplier = useMemo(() => {
        const userInfo = getUserInfo();
        return !!userInfo?.suppId;
    }, []);

    const {
        deliveryStats,
        selectedDate,
        dateOptions,
        isLoading: rawIsLoading,
        handleDateChange,
        refreshData,
    } = useHomePageForRider();

    const isLoading = useDeferredLoading(rawIsLoading, 200);

    // 마운트 후 스크롤 가능한 부모 컨테이너 탐색
    useEffect(() => {
        let el = wrapperRef.current?.parentElement;
        while (el) {
            const overflow = window.getComputedStyle(el).overflowY;
            if (overflow === 'auto' || overflow === 'scroll') {
                scrollContainerRef.current = el;
                break;
            }
            el = el.parentElement;
        }
    }, []);

    const handleRefresh = useCallback(async () => {
        await refreshData();
    }, [refreshData]);

    const { pullDistance, isRefreshing, threshold } = usePullToRefresh(handleRefresh, scrollContainerRef);

    const isToday = selectedDate === new Date().toISOString().split('T')[0];

    const formatDateToYyyymmdd = (dateString: string): string => {
        if (!dateString) return '';
        return dateString.replace(/-/g, '');
    };

    const handleSlotClick = (slotType: string, slotName: string) => {
        if (isSupplier || !selectedDate) {
            return;
        }

        const params = new URLSearchParams();
        params.set('fromDate', formatDateToYyyymmdd(selectedDate));
        params.set('toDate', formatDateToYyyymmdd(selectedDate));
        params.set('slotType', slotType);
        params.set('slotName', slotName);

        navigate(`/delivery-history/detail?${params.toString()}`);
    };

    const handleCalendarClick = () => {
        if (dateRef.current) {
            try {
                dateRef.current.focus();
                if (typeof dateRef.current.showPicker === 'function') {
                    dateRef.current.showPicker();
                } else {
                    dateRef.current.click();
                }
            } catch (error) {
                console.log('Calendar picker not supported:', error);
                dateRef.current.click();
            }
        }
    };

    return (
        <div ref={wrapperRef}>
            {/* Pull-to-refresh 인디케이터 */}
            {(pullDistance > 0 || isRefreshing) && (
                <div
                    className={styles['pull-indicator']}
                    style={{ height: isRefreshing ? 56 : pullDistance }}
                >
                    <div
                        className={`${styles['pull-icon']} ${isRefreshing ? styles['pull-icon-spinning'] : ''}`}
                        style={{ transform: `rotate(${Math.min((pullDistance / threshold) * 180, 180)}deg)` }}
                    >
                        ↻
                    </div>
                    <span className={styles['pull-text']}>
                        {isRefreshing ? '새로고침 중...' : pullDistance >= threshold ? '놓아서 새로고침' : '당겨서 새로고침'}
                    </span>
                </div>
            )}

            {/* 통합 필터 카드 */}
            <div className={styles['filter-card']}>
                {/* 탭 */}
                <div className={styles['tabs']}>
                    <button
                        className={`${styles['tab']} ${activeTab === 'summary' ? styles['tab-active'] : ''}`}
                        onClick={() => onTabChange('summary')}
                    >
                        요약
                    </button>
                    <button
                        className={`${styles['tab']} ${activeTab === 'detail' ? styles['tab-active'] : ''}`}
                        onClick={() => onTabChange('detail')}
                    >
                        상세
                    </button>
                </div>
                {/* 구분선 */}
                <div className={styles['filter-divider']} />
                {/* 날짜 선택 */}
                <div className={styles['date-selector']}>
                    <div className={styles['date-chips']}>
                        {dateOptions && dateOptions.map((date) => (
                            <button
                                key={date.value}
                                className={`${styles['date-chip']} ${selectedDate === date.value ? styles['date-chip-active'] : ''}`}
                                onClick={() => handleDateChange(date.value)}
                            >
                                {date.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles['calendar-btn-wrapper']}>
                        <button className={styles['calendar-btn']} type="button" onClick={handleCalendarClick}>
                            <i className="fas fa-calendar-alt"></i>
                        </button>
                        <input
                            ref={dateRef}
                            type="date"
                            className={styles['calendar-input']}
                            value={selectedDate}
                            onChange={(e) => handleDateChange(e.target.value)}
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            {isLoading ? (
                <div className={styles['loading-section']}>
                    <div className={styles['loading-spinner']}></div>
                    <p className={styles['loading-text']}>데이터를 불러오는 중...</p>
                </div>
            ) : (
                <>
                    {!isToday && <FeeCard deliveryFee={deliveryStats.deliveryFee} />}

                    <StatsCard
                        completedCount={deliveryStats.completedCount}
                        rejectedCount={deliveryStats.rejectedCount}
                        acceptanceRate={deliveryStats.acceptanceRate}
                        foodCount={deliveryStats.foodCount}
                        bmartCount={deliveryStats.bmartCount}
                        storeCount={deliveryStats.storeCount}
                    />

                    <SlotsCard
                        morningLunchPeakCount={deliveryStats.morningLunchPeakCount}
                        afternoonNonPeakCount={deliveryStats.afternoonNonPeakCount}
                        eveningPeakCount={deliveryStats.nightPeakCount}
                        nightPeakCount={deliveryStats.eveningPeakCount}
                        slotNames={deliveryStats.slotNames}
                        slotCounts={deliveryStats.slotCounts}
                        slotTimeRanges={deliveryStats.slotTimeRanges}
                        onSlotClick={handleSlotClick}
                        clickable={!isSupplier}
                    />
                </>
            )}
        </div>
    );
};
